/* eslint-disable react/prop-types */
import { Alert } from 'baseui/icon';
import { useSnackbar } from 'baseui/snackbar';
import {
  Day,
  addDays,
  closestTo,
  differenceInDays,
  nextDay,
  subDays,
} from 'date-fns';
import React, { useMemo } from 'react';
import { Archive, Check, X } from 'react-feather';

import { Button, ButtonProps } from 'components/Button';
import { LinkButton } from 'components/LinkButton';
import { isMutationSuccess } from 'services/api/base-api';
import { ClientService } from 'services/api/clients/endpoints';
import { useSendEstimateMutation } from 'services/api/estimates/endpoints';
import { Estimate } from 'services/api/estimates/types';
import { useSendInvoiceMutation } from 'services/api/invoices/endpoints';
import { Invoice, SentInvoiceDestination } from 'services/api/invoices/types';
import { Job } from 'services/api/jobs/types';
import { WorkRequestStatus } from 'services/api/types.shared';
import { DayConstraint } from 'services/api/work-requests/types';
import { store } from 'store/store';
import { useLocale } from 'utils/hooks/useLocale';

const getNeareastDayToDate = (days: DayConstraint[]) => {
  const dayIndex: Record<DayConstraint, Day> = {
    SUNDAY: 0,
    MONDAY: 1,
    TUESDAY: 2,
    WEDNESDAY: 3,
    THURSDAY: 4,
    FRIDAY: 5,
    SATURDAY: 6,
  };
  const daysToDate = days.map((day) =>
    nextDay(subDays(new Date(), 1), dayIndex[day]),
  );
  return closestTo(new Date(), daysToDate);
};

const ACTION_COMPONENT: Record<
  WorkRequestStatus,
  React.FC<{ workRequest: Job | Estimate | Invoice } & ButtonProps>
> = {
  Draft: () => null,
  // Estimate Actions
  EstCreated: ({ workRequest }) => {
    const { t } = useLocale();
    const closestToConstraint = getNeareastDayToDate(
      workRequest.dateConstraints,
    );

    return (
      <LinkButton
        to={`/schedule?workRequestId=${workRequest.id}${
          closestToConstraint
            ? `&openingDay=${closestToConstraint.toISOString()}&navigateToDate=${closestToConstraint.toISOString()}`
            : ''
        }`}
        size="compact"
        kind="tertiary"
        className="whitespace-nowrap"
      >
        {t('Schedule Estimate')}
      </LinkButton>
    );
  },
  EstScheduled: () => (
    <span className="flex justify-center">
      <Check className="text-green-600" />
    </span>
  ),
  EstStarted: () => <div className="text-center">---</div>,
  EstCompleted: ({ workRequest }) => {
    const { t } = useLocale();
    const [sendEstimate, mutation] = useSendEstimateMutation();
    const snackbar = useSnackbar();

    if (!workRequest.estimateId) {
      return (
        <LinkButton
          to={`/estimates/${workRequest.id}/edit`}
          size="compact"
          kind="tertiary"
          className="whitespace-nowrap"
        >
          {t('Review & Send')}
        </LinkButton>
      );
    }

    return (
      <Button
        size="compact"
        kind="tertiary"
        className="whitespace-nowrap"
        onClick={async () => {
          const result = await sendEstimate({
            workRequestId: workRequest.id,
            dateSent: new Date().toISOString(),
          });
          snackbar.enqueue({
            message: t('estimate_sending_message'),
            progress: true,
          });
          if (isMutationSuccess(result)) {
            snackbar.dequeue();
            snackbar.enqueue({ message: t('estimate_sent_successfully') });
          } else {
            snackbar.dequeue();
            snackbar.enqueue({ message: t('estimate_sent_error') });
          }
        }}
        isLoading={mutation.isLoading}
      >
        {t('Send Estimate')}
      </Button>
    );
  },
  EstSent: ({ workRequest }) => {
    const { t } = useLocale();
    const [sendEstimate, mutation] = useSendEstimateMutation();
    const snackbar = useSnackbar();
    const daysSinceEstimateSent = workRequest.estimateSent
      ? differenceInDays(new Date(workRequest.estimateSent), Date.now())
      : 0;

    const dateSent = workRequest.estimateSent;
    if (daysSinceEstimateSent >= 14 && dateSent) {
      return (
        <Button
          size="compact"
          kind="tertiary"
          className="whitespace-nowrap"
          onClick={async () => {
            const result = await sendEstimate({
              workRequestId: workRequest.id,
              dateSent,
            });
            snackbar.enqueue({
              message: t('estimate_sending_message'),
              progress: true,
            });
            if (isMutationSuccess(result)) {
              snackbar.dequeue();
              snackbar.enqueue({ message: t('estimate_sent_successfully') });
            } else {
              snackbar.dequeue();
              snackbar.enqueue({ message: t('estimate_sent_error') });
            }
          }}
          isLoading={mutation.isLoading}
        >
          {t('Remind')}
        </Button>
      );
    }

    return (
      <span className="flex items-center justify-center space-x-1">
        <Check className="text-green-600" />
        <span>{t('Sent')}</span>
      </span>
    );
  },
  EstApproved: ({ workRequest }) => {
    const { t } = useLocale();
    const closestToConstraint = getNeareastDayToDate(
      workRequest.dateConstraints,
    );
    return (
      <LinkButton
        to={`/schedule?workRequestId=${workRequest.id}${
          closestToConstraint
            ? `&openingDay=${closestToConstraint.toISOString()}&navigateToDate=${closestToConstraint.toISOString()}`
            : ''
        }`}
        size="compact"
        kind="tertiary"
        className="whitespace-nowrap"
      >
        {t('Schedule Job')}
      </LinkButton>
    );
  },
  EstDenied: ({ workRequest }) => {
    const { t } = useLocale();
    return (
      <LinkButton
        to={`/estimates/${workRequest.id}/edit`}
        size="compact"
        kind="tertiary"
        className="whitespace-nowrap"
      >
        {t('Review & Send')}
      </LinkButton>
    );
  },
  // Job Actions
  Created: ({ workRequest }) => {
    const closestToConstraint = getNeareastDayToDate(
      workRequest.dateConstraints,
    );
    const { t } = useLocale();

    return (
      <LinkButton
        to={`/schedule?workRequestId=${workRequest.id}${
          closestToConstraint
            ? `&openingDay=${closestToConstraint.toISOString()}&navigateToDate=${closestToConstraint.toISOString()}`
            : ''
        }`}
        size="compact"
        kind="tertiary"
        className="whitespace-nowrap"
      >
        {t('Schedule Job')}
      </LinkButton>
    );
  },
  Scheduled: () => (
    <span className="flex justify-center">
      <Check className="text-green-600" />
    </span>
  ),
  Started: ({ workRequest, ...buttonProps }) => {
    const { t } = useLocale();
    const [sendInvoice, mutation] = useSendInvoiceMutation();
    const snackbar = useSnackbar();

    return workRequest.invoiceCreatedBy ? (
      <Button
        className="whitespace-nowrap"
        size="compact"
        kind="tertiary"
        {...buttonProps}
        isLoading={mutation.isLoading}
        onClick={async () => {
          const quickBooksRequest = await store.dispatch(
            ClientService.endpoints._isQuickbooksIntegrated.initiate(),
          );
          if (!isMutationSuccess(quickBooksRequest)) {
            return snackbar.enqueue({
              // message: `Failed to export invoice to Quickbooks: ${getErrorMessage(
              //   quickBooksRequest.error,
              // )} `,
              message: t('invoice_quickbooks_export_error'),
              startEnhancer: ({ size }) => <Alert size={size} />,
            });
          }
          const connected = quickBooksRequest.data.isLoggedIn;
          const sendInvoiceTo: SentInvoiceDestination = connected
            ? 'TO-QUICKBOOKS'
            : 'TO-CUSTOMER';

          snackbar.enqueue({
            message: t('invoice_sending'),
            // message: 'Sending Invoice',
            progress: true,
          });
          if (!workRequest.invoiceId) {
            snackbar.dequeue();
            return snackbar.enqueue({
              message:
                'Invoice not created. Invoice must be created before sent.',
            });
          }

          const invoiceSent = isMutationSuccess(
            await sendInvoice({
              workRequestId: workRequest.id,
              dateSent: new Date().toISOString(),
              dateDue: addDays(new Date(), 14).toISOString(),
              invoiceTo: sendInvoiceTo,
            }),
          );
          if (invoiceSent) {
            snackbar.dequeue();
            snackbar.enqueue({
              // message: 'Invoice Sent'
              message: t('invoice_sent_successfully'),
            });
          } else {
            snackbar.dequeue();
            snackbar.enqueue({
              // message: 'Failed to send invoice'
              message: t('invoice_sent_error'),
            });
          }
        }}
        disabled={
          workRequest.invoiceTotal === 0 ||
          (workRequest as any).breakdown?.quickbooksBalance === 0
        }
        title={
          workRequest.invoiceTotal === 0 ||
          (workRequest as any).breakdown?.quickbooksBalance === 0
            ? t('invoices_description_total_zero')
            : undefined
        }
      >
        {t('Send Invoice')}
      </Button>
    ) : (
      <LinkButton
        to={`/invoice/new?workRequestId=${workRequest.id}`}
        size={buttonProps.size ?? 'compact'}
        kind={buttonProps.kind ?? 'tertiary'}
        className="whitespace-nowrap"
      >
        {t('Create Invoice')}
      </LinkButton>
    );
  },
  Completed: ({ workRequest, ...buttonProps }) => {
    const { t } = useLocale();
    const [sendInvoice, mutation] = useSendInvoiceMutation();
    const snackbar = useSnackbar();

    return workRequest.invoiceCreatedBy ? (
      <Button
        className="whitespace-nowrap"
        size="compact"
        kind="tertiary"
        {...buttonProps}
        isLoading={mutation.isLoading}
        onClick={async () => {
          const quickBooksRequest = await store.dispatch(
            ClientService.endpoints._isQuickbooksIntegrated.initiate(),
          );
          if (!isMutationSuccess(quickBooksRequest)) {
            return snackbar.enqueue({
              // message: `Failed to export invoice to Quickbooks: ${getErrorMessage(
              //   quickBooksRequest.error,
              // )} `,
              message: t('invoice_quickbooks_export_error'),
              startEnhancer: ({ size }) => <Alert size={size} />,
            });
          }
          const connected = quickBooksRequest.data.isLoggedIn;
          const sendInvoiceTo: SentInvoiceDestination = connected
            ? 'TO-QUICKBOOKS'
            : 'TO-CUSTOMER';

          snackbar.enqueue({
            message: t('invoice_sending'),
            // message: 'Sending Invoice',
            progress: true,
          });
          if (!workRequest.invoiceId) {
            snackbar.dequeue();
            return snackbar.enqueue({
              message:
                'Invoice not created. Invoice must be created before sent.',
            });
          }

          const invoiceSent = isMutationSuccess(
            await sendInvoice({
              workRequestId: workRequest.id,
              dateSent: new Date().toISOString(),
              dateDue: addDays(new Date(), 14).toISOString(),
              invoiceTo: sendInvoiceTo,
            }),
          );
          if (invoiceSent) {
            snackbar.dequeue();
            snackbar.enqueue({
              // message: 'Invoice Sent'
              message: t('invoice_sent_successfully'),
            });
          } else {
            snackbar.dequeue();
            snackbar.enqueue({
              // message: 'Failed to send invoice'
              message: t('invoice_sent_error'),
            });
          }
        }}
        disabled={
          workRequest.invoiceTotal === 0 ||
          (workRequest as any).breakdown?.quickbooksBalance === 0
        }
        title={
          workRequest.invoiceTotal === 0 ||
          (workRequest as any).breakdown?.quickbooksBalance === 0
            ? t('invoices_description_total_zero')
            : undefined
        }
      >
        {t('Send Invoice')}
      </Button>
    ) : (
      <LinkButton
        to={`/invoice/new?workRequestId=${workRequest.id}`}
        size={buttonProps.size ?? 'compact'}
        kind={buttonProps.kind ?? 'tertiary'}
        className="whitespace-nowrap"
      >
        {t('Create Invoice')}
      </LinkButton>
    );
  },

  //   Invoice Actions
  InvoiceCreated: ({ workRequest, ...buttonProps }) => {
    const { t } = useLocale();
    const [sendInvoice, mutation] = useSendInvoiceMutation();
    const snackbar = useSnackbar();
    return (
      <Button
        className="whitespace-nowrap"
        size="compact"
        kind="tertiary"
        {...buttonProps}
        isLoading={mutation.isLoading}
        onClick={async () => {
          const quickBooksRequest = await store.dispatch(
            ClientService.endpoints._isQuickbooksIntegrated.initiate(),
          );
          if (!isMutationSuccess(quickBooksRequest)) {
            return snackbar.enqueue({
              // message: `Failed to export invoice to Quickbooks: ${getErrorMessage(
              //   quickBooksRequest.error,
              // )} `,
              message: t('invoice_quickbooks_export_error'),
              startEnhancer: ({ size }) => <Alert size={size} />,
            });
          }
          const connected = quickBooksRequest.data.isLoggedIn;
          const sendInvoiceTo: SentInvoiceDestination = connected
            ? 'TO-QUICKBOOKS'
            : 'TO-CUSTOMER';

          snackbar.enqueue({
            message: t('invoice_sending'),
            // message: 'Sending Invoice',
            progress: true,
          });
          if (!workRequest.invoiceId) {
            snackbar.dequeue();
            return snackbar.enqueue({
              message:
                'Invoice not created. Invoice must be created before sent.',
            });
          }

          const invoiceSent = isMutationSuccess(
            await sendInvoice({
              workRequestId: workRequest.id,
              dateSent: new Date().toISOString(),
              dateDue: addDays(new Date(), 14).toISOString(),
              invoiceTo: sendInvoiceTo,
            }),
          );
          if (invoiceSent) {
            snackbar.dequeue();
            snackbar.enqueue({
              // message: 'Invoice Sent'
              message: t('invoice_sent_successfully'),
            });
          } else {
            snackbar.dequeue();
            snackbar.enqueue({
              // message: 'Failed to send invoice'
              message: t('invoice_sent_error'),
            });
          }
        }}
        disabled={
          workRequest.invoiceTotal === 0 ||
          (workRequest as any).breakdown?.quickbooksBalance === 0
        }
        title={
          workRequest.invoiceTotal === 0 ||
          (workRequest as any).breakdown?.quickbooksBalance === 0
            ? t('invoices_description_total_zero')
            : undefined
        }
      >
        {t('Send Invoice')}
      </Button>
    );
  },
  InvoiceSent: ({ workRequest }) => {
    const { t } = useLocale();
    const [sendInvoice, mutation] = useSendInvoiceMutation();
    const snackbar = useSnackbar();
    const daysSinceEstimateSent = workRequest.invoiceSent
      ? differenceInDays(new Date(workRequest.invoiceSent), Date.now())
      : 0;

    if (
      daysSinceEstimateSent >= 14 &&
      workRequest.invoiceDue &&
      workRequest.invoiceSent
    ) {
      const dateDue = workRequest.invoiceDue;
      const dateSent = workRequest.invoiceSent;
      return (
        <Button
          className="whitespace-nowrap"
          size="compact"
          kind="tertiary"
          isLoading={mutation.isLoading}
          onClick={async () => {
            const result = await sendInvoice({
              workRequestId: workRequest.id,
              dateSent,
              dateDue,
            });
            snackbar.enqueue({
              message: t('invoice_sending'),
              // message: 'Sending Invoice'
              progress: true,
            });
            if (isMutationSuccess(result)) {
              snackbar.dequeue();
              snackbar.enqueue({
                message: t('invoice_sent_successfully'),
                // message: 'Invoice Sent'
              });
            } else {
              snackbar.dequeue();
              snackbar.enqueue({
                message: t('invoice_sent_error'),
                // message: 'Failed to send invoice'
              });
            }
          }}
        >
          {t('Remind')}
        </Button>
      );
    }

    return (
      <LinkButton
        to={`/invoice/${workRequest.id}/edit`}
        size="compact"
        kind="tertiary"
        className="whitespace-nowrap"
      >
        {t('Review & Mark')}
      </LinkButton>
    );
  },
  Collected: () => {
    const { t } = useLocale();
    return (
      <span className="flex items-center justify-center space-x-1">
        <Check className="text-green-600" />
        <span>{t('Paid')}</span>
      </span>
    );
  },
  Cancelled: () => {
    const { t } = useLocale();
    return (
      <span className="flex items-center justify-center space-x-1">
        <X className="text-red-600" />
        <span>{t('Cancelled')}</span>
      </span>
    );
  },
  Archived: () => {
    const { t } = useLocale();
    return (
      <span className="flex items-center justify-center space-x-1">
        <Archive className="text-gray-600" />
        <span>{t('Archived')}</span>
      </span>
    );
  },
};

export default function StatusAction({
  workRequest,
  ...buttonProps
}: {
  workRequest: Job | Estimate | Invoice;
} & ButtonProps) {
  const progress = workRequest.progress;
  const Component = useMemo(() => ACTION_COMPONENT[progress], [progress]);

  return <Component workRequest={workRequest} {...buttonProps} />;
}
